import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, Loader2, ShieldAlert, Baby, Sun, Moon, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/lib/theme";

export default function Login() {
  const [userType, setUserType] = useState("doctor");
  const [wardMode, setWardMode] = useState("nicu");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { dark, toggle } = useTheme();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      const u = await login(username.trim(), password, userType, mfaRequired ? otp : undefined);
      toast.success(`Welcome back, ${u.full_name || u.username}`);
      if (wardMode === "ward") { localStorage.setItem("ward_mode", "1"); nav("/ward", { replace: true }); return; }
      localStorage.removeItem("ward_mode");
      const dest = u.role === "admin" ? "/admin" : u.role === "nurse" ? "/nursing" : u.role === "doctor" ? "/rounds" : "/dashboard";
      nav(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = err?.response?.headers?.["x-auth-reason"];
      if (detail === "MFA required" || reason === "mfa_required") {
        setMfaRequired(true);
        toast.info("MFA required — enter the 6-digit code from your authenticator");
      } else if (detail === "Invalid MFA code") {
        toast.error("Invalid MFA code");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr]">
      <div className="login-bg relative hidden lg:flex flex-col justify-between p-14 text-white overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <Baby size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-white/70">Neonatal EMR</div>
            <div className="font-display text-2xl font-semibold">NICU Care Suite</div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="text-xs uppercase tracking-[0.28em] text-white/70 mb-3">Built for pediatric intensive care</div>
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05]">
            Every heartbeat. <br /> Every fluid. <br /> One quiet interface.
          </h1>
          <p className="text-white/80 mt-6 max-w-md">
            Autosaved doctor rounds, hourly nursing charts, real-time bed map, and neonatal fluid calculations —
            engineered so a full NICU round takes under two minutes.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            {[
              { k: "Voice", v: "Dictate notes hands-free" },
              { k: "Trends", v: "Weight, bilirubin, vitals" },
              { k: "Offline", v: "Works without internet" },
            ].map((f) => (
              <div key={f.k} className="rounded-xl bg-white/10 border border-white/15 backdrop-blur px-4 py-3">
                <div className="text-white/70 text-[11px] uppercase tracking-widest">{f.k}</div>
                <div className="font-medium mt-1">{f.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/60">© {new Date().getFullYear()} NICU Care Suite • v0.1 preview</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center">
                <Stethoscope size={18} className="text-primary" />
              </div>
              <span className="font-display font-semibold text-lg">NICU EMR</span>
            </div>
            <button data-testid="theme-toggle" onClick={toggle}
                    className="h-9 w-9 rounded-lg border border-border grid place-items-center text-muted-foreground hover:text-foreground"
                    aria-label="Toggle theme">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>

          <h2 className="font-display text-3xl font-semibold mb-1">Sign in</h2>
          <p className="text-muted-foreground text-sm mb-8">Select your role and enter your credentials to continue.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label>Login to</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="login-ward-mode">
                {[{ v: "nicu", l: "NICU", d: "Full EMR system" }, { v: "ward", l: "PICU / General Ward", d: "Billing only" }].map((o) => (
                  <button type="button" key={o.v} data-testid={`login-ward-${o.v}`} onClick={() => setWardMode(o.v)}
                          className={`rounded-lg border px-3 py-2 text-left transition-colors ${wardMode === o.v ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"}`}>
                    <div className="text-sm font-medium">{o.l}</div>
                    <div className="text-[11px] text-muted-foreground">{o.d}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="usertype">User type</Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger id="usertype" data-testid="login-usertype" className="h-11">
                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="nurse">Nursing Staff</SelectItem>
                  <SelectItem value="receptionist">Receptionist</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input id="username" data-testid="login-username" className="h-11" autoComplete="username"
                     value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. dr.arya" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" className="text-xs text-primary hover:underline"
                  onClick={() => toast.info("Contact your NICU administrator to reset your password.")}
                  data-testid="login-forgot">Forgot password?</button>
              </div>
              <Input id="password" data-testid="login-password" className="h-11" type="password" autoComplete="current-password"
                     value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>

            {mfaRequired && (
              <div className="space-y-2">
                <Label htmlFor="otp" className="flex items-center gap-2"><KeyRound size={14} /> 6-digit MFA code</Label>
                <Input id="otp" data-testid="login-otp" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                       className="h-11 tabular text-lg tracking-[0.4em] font-semibold text-center"
                       value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="123456" />
              </div>
            )}

            <Button type="submit" data-testid="login-submit"
                    disabled={loading}
                    className="w-full h-11 rounded-full text-base font-medium">
              {loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Signing in…</> : mfaRequired ? "Verify & continue" : "Sign in"}
            </Button>

            <div className="flex items-start gap-2 rounded-lg bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/25 px-3 py-2 text-xs text-primary dark:text-primary">
              <Stethoscope size={14} className="mt-0.5 shrink-0" />
              <div>
                Developed by <b>Dr. Chaitanya Kumar</b> · +91-7992303258 · <a href="mailto:dr.chaitanyaped@gmail.com" className="underline hover:no-underline">dr.chaitanyaped@gmail.com</a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
